import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InlineSpinner } from '../Buttons/InlineSpinner';

import { CodeInput } from './CodeInput';
import type { CodeInputAlign } from './CodeInput';
import { MESSAGE_LINE_HEIGHT, lineSx, messageSx, quietTextSx } from './codeMessageStyles';
import { CodeRefusal } from './CodeRefusal';
import { ResendCode } from './ResendCode';

export interface CodeEntryProps {
  readonly label: string;
  readonly labelledBy?: string;
  readonly describedBy?: string;
  readonly length: number;
  readonly code: string;
  readonly verifying: boolean;
  readonly verifyingText?: string;
  readonly errorText: string | null;
  readonly detailText?: string | null;
  readonly resendAt: number | null;
  readonly resending?: boolean;
  readonly onCodeChange: (code: string) => void;
  readonly onComplete: (code: string) => void;
  readonly onResend: () => void;
  readonly align?: CodeInputAlign;
  readonly attemptKey?: number;
  readonly resetKey?: number;
  readonly alternative?: React.ReactNode;
  readonly besideResend?: React.ReactNode;
}

const slotSx = (theme: Theme): SystemStyleObject<Theme> => ({
  minBlockSize: `calc(${theme.typography.highlight.fontSize} * ${MESSAGE_LINE_HEIGHT} + ${theme.typography.label.fontSize} * ${MESSAGE_LINE_HEIGHT} + ${theme.spacing(1)})`,
});

const rootSx: SystemStyleObject<Theme> = { display: 'flex', flexDirection: 'column', gap: 2 };

const resendRowSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  columnGap: 4,
  rowGap: 2,
};

interface MessageSlotProps {
  readonly align: CodeInputAlign;
  readonly verifying: boolean;
  readonly verifyingText?: string;
  readonly errorId: string;
  readonly errorText: string | null;
  readonly detailText: string | null;
}

const MessageSlot = ({ align, verifying, verifyingText, errorId, errorText, detailText }: MessageSlotProps) => {
  const { t } = useTranslation();
  return (
    <Box sx={slotSx}>
      <Box role="status" sx={[messageSx(align), lineSx]}>
        {verifying && (
          <>
            <InlineSpinner aria-hidden />
            <Typography variant="label" component="span" sx={quietTextSx}>
              {verifyingText ?? t('coreComponents.codeInput.checking', 'Checking the code…')}
            </Typography>
          </>
        )}
      </Box>
      {!verifying && errorText !== null && <CodeRefusal id={errorId} message={errorText} detail={detailText} align={align} />}
    </Box>
  );
};

export const CodeEntry: React.FC<CodeEntryProps> = ({
  label,
  labelledBy,
  describedBy,
  length,
  code,
  verifying,
  verifyingText,
  errorText,
  detailText = null,
  resendAt,
  resending = false,
  onCodeChange,
  onComplete,
  onResend,
  align = 'start',
  attemptKey,
  resetKey,
  alternative,
  besideResend,
}) => {
  const errorId = useId();
  const [editedAttempt, setEditedAttempt] = useState<number | null>(null);
  const refused = errorText !== null;
  const paintRefused = refused && (attemptKey === undefined || editedAttempt !== attemptKey);
  const errorShown = refused && !verifying;

  const handleCodeChange = (next: string): void => {
    if (attemptKey !== undefined) setEditedAttempt(attemptKey);
    onCodeChange(next);
  };

  const resend = <ResendCode resendAt={resendAt} busy={resending} disabled={verifying} onResend={onResend} align={align} />;

  return (
    <Box sx={rootSx}>
      <CodeInput
        key={`${resetKey ?? 0}:${attemptKey ?? 0}`}
        label={label}
        labelledBy={labelledBy}
        describedBy={describedBy}
        length={length}
        value={code}
        onChange={handleCodeChange}
        onComplete={onComplete}
        invalid={paintRefused}
        errorId={errorShown ? errorId : undefined}
        readOnly={verifying}
        align={align}
        shake={attemptKey !== undefined && attemptKey > 0}
        autoFocus
      />
      <MessageSlot
        align={align}
        verifying={verifying}
        verifyingText={verifyingText}
        errorId={errorId}
        errorText={errorText}
        detailText={detailText}
      />
      {alternative}
      {besideResend === undefined ? (
        resend
      ) : (
        <Box sx={resendRowSx}>
          {resend}
          {besideResend}
        </Box>
      )}
    </Box>
  );
};
